// Nova Class — istemci ve sunucunun paylaştığı tipler ve WebSocket mesaj sözleşmesi.
// Tüm koordinatlar sayfa koordinatıdır: PDF.js'in scale=1 görünüm uzayı (birim = PDF puntosu,
// sol üst köşe orijin, y aşağı doğru). Böylece her istemci kendi yakınlaştırmasıyla çizer,
// veri herkes için aynı kalır ve PDF'ye birebir işlenebilir.

export type Role = 'teacher' | 'student';

/** [x, y, basınç(0..1)] — fare/dokunma için basınç 0.5 sabittir. */
export type Point = [number, number, number];

export type InkTool = 'pen' | 'highlighter';
export const POLYGON_KINDS = ['triangle', 'rightTriangle', 'trapezoid', 'parallelogram', 'rhombus', 'pentagon', 'hexagon', 'octagon'] as const;
export type PolygonKind = (typeof POLYGON_KINDS)[number];
export const SHAPE_KINDS = ['line', 'arrow', 'rect', 'ellipse', ...POLYGON_KINDS] as const;
export type ShapeKind = (typeof SHAPE_KINDS)[number];

interface AnnotationBase {
  id: string;
  page: number;
  authorId: string;
  authorName: string;
  authorRole: Role;
  createdAt: number;
}

export interface InkAnnotation extends AnnotationBase {
  kind: 'ink';
  tool: InkTool;
  color: string;
  size: number;
  points: Point[];
}

export interface ShapeAnnotation extends AnnotationBase {
  kind: 'shape';
  shape: ShapeKind;
  color: string;
  size: number;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

export interface TextAnnotation extends AnnotationBase {
  kind: 'text';
  color: string;
  fontSize: number;
  /** Metin kutusunun sol üst köşesi. */
  x: number;
  y: number;
  text: string;
}

export const FIGURE_NAMES = ['integral', 'sum', 'product', 'fraction', 'root', 'power', 'limit', 'numberLine', 'coordinatePlane'] as const;
export type FigureName = (typeof FIGURE_NAMES)[number];
/** Konumlar şeklin sol üst köşesine göredir; y yazının taban çizgisidir. */
export interface FigureText {
  t: string;
  x: number;
  y: number;
  s: number;
}
/** [x1, y1, x2, y2, kalınlık] */
export type FigureLine = [number, number, number, number, number];

/** Formül, sayı doğrusu ve koordinat düzlemi: oluşturulurken hesaplanmış yazı ve çizgi grubu. */
export interface FigureAnnotation extends AnnotationBase {
  kind: 'figure';
  name: FigureName;
  color: string;
  x: number;
  y: number;
  w: number;
  h: number;
  texts: FigureText[];
  lines: FigureLine[];
}

export type Annotation = InkAnnotation | ShapeAnnotation | TextAnnotation | FigureAnnotation;

type AuthorFields = 'authorId' | 'authorName' | 'authorRole' | 'createdAt';
export type ShapeInput = Omit<ShapeAnnotation, AuthorFields>;
export type TextInput = Omit<TextAnnotation, AuthorFields>;
export type FigureInput = Omit<FigureAnnotation, AuthorFields>;
export type AnnotationInput = ShapeInput | TextInput | FigureInput;
export type ShapePreview = Omit<ShapeInput, 'kind' | 'id'>;

export interface Participant {
  id: string;
  name: string;
  role: Role;
  online: boolean;
  /** Öğretmenin bu öğrenciye tek tek verdiği çizim izni. */
  canDraw: boolean;
  handRaised: boolean;
}

export interface LessonInfo {
  id: string;
  title: string;
  pageCount: number;
  pdfName: string;
}

export interface LessonSettings {
  /** Açıksa tüm öğrenciler çizebilir. */
  studentsCanDraw: boolean;
  /** Açıksa öğrenciler öğretmenin sayfasından ayrılamaz. */
  followLocked: boolean;
  teacherPage: number;
}

export interface LaserPoint {
  page: number;
  x: number;
  y: number;
}

export type HelloMsg = { t: 'hello'; lessonId: string; clientId: string; name: string; teacherKey?: string };

export type ClientMsg =
  | HelloMsg
  | { t: 'ink:start'; id: string; page: number; tool: InkTool; color: string; size: number; point: Point }
  | { t: 'ink:append'; id: string; points: Point[] }
  | { t: 'ink:end'; id: string }
  | { t: 'ann:add'; a: AnnotationInput }
  | { t: 'ann:delete'; ids: string[] }
  | { t: 'ann:restore'; ids: string[] }
  | { t: 'page:clear'; page: number }
  | { t: 'nav'; page: number }
  | { t: 'settings'; studentsCanDraw?: boolean; followLocked?: boolean }
  | { t: 'perm'; participantId: string; canDraw: boolean }
  | { t: 'hand'; raised: boolean }
  | { t: 'preview'; p: ShapePreview | null }
  | { t: 'laser'; p: LaserPoint | null };

export interface InitMsg {
  t: 'init';
  self: Participant;
  lesson: LessonInfo;
  settings: LessonSettings;
  participants: Participant[];
  annotations: Annotation[];
  /** Bağlanma anında hâlâ çizilmekte olan kalem izleri. */
  drafts: InkAnnotation[];
}

export type ServerMsg =
  | InitMsg
  | { t: 'ink:start'; a: InkAnnotation }
  | { t: 'ink:append'; id: string; points: Point[] }
  | { t: 'ink:end'; a: InkAnnotation }
  | { t: 'ann:add'; list: Annotation[] }
  | { t: 'ann:delete'; ids: string[] }
  | { t: 'settings'; s: LessonSettings }
  | { t: 'participants'; list: Participant[] }
  | { t: 'preview'; userId: string; p: ShapePreview | null }
  | { t: 'laser'; userId: string; p: LaserPoint | null }
  | { t: 'denied'; reason: string; ids?: string[] }
  | { t: 'fatal'; reason: string };

/** WebSocket kapanış kodları: 44xx = tekrar denenmemeli. */
export const CLOSE = { badRequest: 4400, badKey: 4401, forbidden: 4403, notFound: 4404 } as const;

export const LIMITS = {
  maxPdfBytes: 30 * 1024 * 1024, // değişirse deploy/nginx/nova-class.conf içindeki client_max_body_size da değişmeli
  maxPointsPerStroke: 20_000,
  maxPointsPerAppend: 1_000,
  maxTextLength: 2_000,
  maxAnnotationsPerLesson: 50_000,
  maxNameLength: 40,
  maxTitleLength: 120,
  maxFigureTexts: 150,
  maxFigureLines: 300,
  maxFigureTextLength: 200,
} as const;

export function effectiveCanDraw(
  p: Pick<Participant, 'role' | 'canDraw'>,
  s: Pick<LessonSettings, 'studentsCanDraw'>,
): boolean {
  return p.role === 'teacher' || s.studentsCanDraw || p.canDraw;
}
